import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip, Empty } from 'antd';
import moment from 'moment';

/**
 * 日历热力图（GitHub 贡献图布局 + 一格多类型堆叠）
 *
 * 自研 SVG 实现，不依赖图表库：
 * @ant-design/plots 的 Heatmap 只有 polygon/density 两种模式，
 * 且「一条数据 = 一个颜色」，无法表达同一天多种打卡类型的堆叠格。
 *
 * ⚠️ 自适应（autoFit）对宿主有硬要求：外层必须是「宽度由父级决定」的块级容器。
 * 放进 antd <Space>（display:inline-flex）、fit-content 或任何宽度由内容撑开的
 * 容器里，会形成测量反馈环：svg 撑开宿主 → 量到的是 svg 自己 → 反推出同一个
 * 边长 → 永久锁死在兜底宽度，既不跟窗口缩也会撑破父级。
 *
 * weeks 结构：
 * [{ weekStart: 'YYYY-MM-DD', days: [{ date, dayOfWeek: 1-7, counts: { 类型key: 次数 } }] }]
 * types 结构：[{ key, label, color }]，数组顺序即格子内自上而下的堆叠顺序
 */

const EMPTY_FILL = '#f5f5f5';
const ROW_LABELS = ['一', '', '三', '', '五', '', '日'];
const DAY_NAMES = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];

/**
 * 自适应几何计算（纯函数，便于单测）
 *
 * 思路：固定「周数」这个语义，反推格子边长，而不是固定像素宽。
 *   占宽 W(s,g) = left + n*(s+g) + g
 *   给定容器宽 cw 能放下的最大整数边长 s = floor((cw - left - g*(n+1)) / n)
 *
 * 三段兜底：
 *   1. 正常区间   边长随容器变，正好铺满，无留白
 *   2. 撞 maxCell 宽容器下格子不该无限变大 → 把余量摊给行列间隙；
 *      还摊不完（超宽屏）就交给外层居中，对称留白比右侧一大块空白好看得多
 *   3. 撞 minCell 窄容器放不下 → 保持最小可读边长，外层横向滚动
 */
export const computeLayout = ({
  containerWidth,
  weeksCount,
  cellSize,
  autoFit,
  minCell,
  maxCell,
  cellGap,
  maxCellGap,
  left,
  top,
}) => {
  const n = Math.max(1, weeksCount);
  const widthOf = (size, gp) => left + n * (size + gp) + gp;
  const heightOf = (size, gp) => top + 7 * (size + gp) + gp;

  // 未开启自适应 / 首帧还没量到宽度：用固定边长
  if (!autoFit || !containerWidth) {
    const width = widthOf(cellSize, cellGap);
    return {
      size: cellSize,
      gap: cellGap,
      width,
      height: heightOf(cellSize, cellGap),
      scroll: !!containerWidth && width > containerWidth,
      fitted: false,
    };
  }

  const ideal = Math.floor((containerWidth - left - cellGap * (n + 1)) / n);
  const size = Math.min(maxCell, Math.max(minCell, ideal));
  let gap = cellGap;

  // 格子已封顶还有余量 → 把余量摊给间隙（不超过 maxCellGap）
  if (widthOf(size, gap) < containerWidth) {
    gap = Math.min(
      maxCellGap,
      Math.floor((containerWidth - left - n * size) / (n + 1)),
    );
  }

  const width = widthOf(size, gap);
  return {
    size,
    gap,
    width,
    height: heightOf(size, gap),
    scroll: width > containerWidth,
    fitted: true,
  };
};

/** 量容器宽度；SSR 下 useEffect 不执行，天然安全 */
const useContainerWidth = (ref) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
};

/**
 * 月份标签：每列（自然周）打一个「这列主要属于哪个月」，月份变了才写新标签。
 *
 * 为什么不按「周一落在哪个月」（旧做法）：
 * 52 周窗口起点是 2025-10-02（周四），首列周一是 09-29。
 * 旧规则给首列写了「9月」，可那一列在窗口内的只有 10-02/03/04 三天，
 * 9 月那三天（09-29、09-30、10-01）根本不在查询范围——等于给一个不存在的月份打了标签，
 * 还跟下一列的「10月」挤成「9月10月」。
 *
 * 新规则：
 *   1. 只统计窗口内的天（rangeStart/rangeEnd 不传就当整列都在范围内）；
 *   2. 取这列里天数占多的月份，天数相同时取靠后的那个月；
 *   3. 与上一个标签至少隔 MIN_LABEL_GAP 列，避免两个标签叠在一起；
 *      因此只占 1 列的“边角月”（开头 3 天的 9月）会被自然掉掉，
 *      而正常的月（≈4.3 列）不会被误掉。
 *
 * 纯函数，便于单测。
 */
export const MIN_LABEL_GAP = 3;

export const buildMonthLabels = ({ weeks, rangeStart, rangeEnd, minGap }) => {
  const gap = minGap === undefined ? MIN_LABEL_GAP : minGap;
  const lo = rangeStart ? moment(rangeStart, 'YYYY-MM-DD') : null;
  const hi = rangeEnd ? moment(rangeEnd, 'YYYY-MM-DD') : null;
  const labels = [];

  (weeks || []).forEach((week, weekIdx) => {
    if (!week || !week.weekStart) return;
    const start = moment(week.weekStart, 'YYYY-MM-DD');
    // 统计这一列里「窗口内」各月份占几天
    const tally = new Map();
    for (let i = 0; i < 7; i += 1) {
      const d = start.clone().add(i, 'days');
      if (lo && d.isBefore(lo, 'day')) continue;
      if (hi && d.isAfter(hi, 'day')) continue;
      const key = `${d.year()}-${d.month()}`;
      const cur = tally.get(key) || {
        key,
        month: d.month(),
        name: d.format('M\u6708'),
        count: 0,
        order: i,
      };
      cur.count += 1;
      cur.order = i;
      tally.set(key, cur);
    }
    if (tally.size === 0) return;

    // 天数占多的月份胜出；天数相同时取靠后（更接近列尾）的那个月
    let best = null;
    tally.forEach((v) => {
      if (
        !best ||
        v.count > best.count ||
        (v.count === best.count && v.order > best.order)
      ) {
        best = v;
      }
    });

    const prev = labels[labels.length - 1];
    if (prev && prev.key === best.key) return; // 同一月不重复标
    if (prev && weekIdx - prev.weekIdx < gap) return; // 太挤就不硬塞，下一列同月还会来候选
    labels.push({ weekIdx, key: best.key, month: best.month, name: best.name });
  });

  return labels;
};

const CalendarHeatmap = ({
  weeks,
  types,
  cellSize,
  gap,
  mode,
  singleColor,
  maxCount,
  autoFit,
  minCell,
  maxCell,
  cellGap,
  maxCellGap,
  onDateClick,
  loading,
  rangeStart,
  rangeEnd,
}) => {
  const [hoverKey, setHoverKey] = useState(null);
  /**
   * 图例悬停锁定：鼠标停在底部某个分类上时，热力图只保留该分类的正常颜色，
   * 其余分类一律降到 0.1 透明度（用来回答「这一类的分布长什么样」）。
   */
  const [focusType, setFocusType] = useState(null);

  const wrapRef = useRef(null);
  const containerWidth = useContainerWidth(wrapRef);

  const LEFT_MARGIN = 30;
  const TOP_MARGIN = 22;
  const SEG_GAP = gap;

  const {
    size,
    gap: gp,
    width: svgWidth,
    height: svgHeight,
    scroll,
  } = useMemo(
    () =>
      computeLayout({
        containerWidth,
        weeksCount: weeks.length,
        cellSize,
        autoFit,
        minCell,
        maxCell,
        cellGap,
        maxCellGap,
        left: LEFT_MARGIN,
        top: TOP_MARGIN,
      }),
    [
      containerWidth,
      weeks.length,
      cellSize,
      autoFit,
      minCell,
      maxCell,
      cellGap,
      maxCellGap,
    ],
  );

  // 月份标签：见 buildMonthLabels（按列内「占多数」的月份打，不是按周一所在月）
  const monthLabels = useMemo(
    () => buildMonthLabels({ weeks, rangeStart, rangeEnd }),
    [weeks, rangeStart, rangeEnd],
  );

  // 命中的类型（保持 types 声明顺序）
  const hitTypes = (counts) =>
    types.filter((t) => Number(counts?.[t.key] || 0) > 0);

  if (!weeks || weeks.length === 0) {
    return <Empty description="暂无打卡数据" />;
  }

  return (
    <div
      ref={wrapRef}
      style={{
        // width:100% + minWidth:0 让本层在 flex 宿主里不被内容反向撑开
        width: '100%',
        minWidth: 0,
        overflowX: scroll ? 'auto' : 'visible',
        opacity: loading ? 0.5 : 1,
        transition: 'opacity 0.2s ease',
      }}
    >
      {/* 自适应后若仍有富余宽度（超宽屏），居中比右侧空一块好看 */}
      <svg
        width={svgWidth}
        height={svgHeight}
        style={{ display: 'block', margin: scroll ? 0 : '0 auto' }}
      >
        {/* 月份 */}
        {monthLabels.map((label) => (
          <text
            key={label.name + label.weekIdx}
            x={LEFT_MARGIN + label.weekIdx * (size + gp)}
            y={13}
            fontSize={12}
            fill="#8c8c8c"
          >
            {label.name}
          </text>
        ))}

        {/* 星期（稀疏：一 三 五 日） */}
        {ROW_LABELS.map((day, idx) =>
          day ? (
            <text
              key={day}
              x={0}
              y={TOP_MARGIN + idx * (size + gp) + size - 3}
              fontSize={12}
              fill="#8c8c8c"
            >
              {day}
            </text>
          ) : null,
        )}

        {/* 格子 */}
        {weeks.map((week, weekIdx) => {
          const dayMap = {};
          (week.days || []).forEach((d) => {
            dayMap[d.dayOfWeek] = d;
          });

          return Array.from({ length: 7 }, (_, dayIdx) => {
            const dayData = dayMap[dayIdx + 1];
            const x = LEFT_MARGIN + weekIdx * (size + gp);
            const y = TOP_MARGIN + dayIdx * (size + gp);

            // 空格子：不套 Tooltip，避免 364 个组件实例
            if (!dayData) {
              return (
                <rect
                  key={`${weekIdx}-${dayIdx}`}
                  x={x}
                  y={y}
                  width={size}
                  height={size}
                  rx={2}
                  ry={2}
                  fill={EMPTY_FILL}
                />
              );
            }

            const hits = hitTypes(dayData.counts);
            const key = dayData.date;
            const isHover = hoverKey === key;
            const total = hits.reduce(
              (s, t) => s + Number(dayData.counts[t.key] || 0),
              0,
            );

            // 堆叠分段：高度均分，段间留缝
            const segH =
              hits.length > 0
                ? (size - SEG_GAP * (hits.length - 1)) / hits.length
                : size;

            const cellNode = (
              <g
                onMouseEnter={() => setHoverKey(key)}
                onMouseLeave={() => setHoverKey(null)}
                style={{ cursor: onDateClick ? 'pointer' : 'default' }}
                onClick={() => onDateClick?.(dayData)}
              >
                {isHover && (
                  <rect
                    x={x - 2}
                    y={y - 2}
                    width={size + 4}
                    height={size + 4}
                    rx={3}
                    ry={3}
                    fill="none"
                    stroke="#1890ff"
                    strokeWidth={1.5}
                    strokeOpacity={0.7}
                  />
                )}
                {hits.length === 0 ? (
                  <rect
                    x={x}
                    y={y}
                    width={size}
                    height={size}
                    rx={2}
                    ry={2}
                    fill={EMPTY_FILL}
                  />
                ) : mode === 'single' ? (
                  /* 单色模式：整格一个矩形，深浅表当日总次数 */
                  <rect
                    x={x}
                    y={y}
                    width={size}
                    height={size}
                    rx={2}
                    ry={2}
                    fill={singleColor}
                    opacity={0.3 + 0.7 * Math.min(1, (total - 1) / maxCount)}
                    style={{ filter: isHover ? 'brightness(0.85)' : 'none' }}
                  />
                ) : (
                  hits.map((t, i) => (
                    <rect
                      key={t.key}
                      x={x}
                      y={y + i * (segH + SEG_GAP)}
                      width={size}
                      height={segH}
                      /**
                       * 分段之间是有间隙的独立小块（不是拼在一起的一整块），
                       * 所以每一段都要圆角；段很薄时按高度一半收敛，避免圆角把矩形扣没了。
                       */
                      rx={Math.min(2, segH / 2)}
                      ry={Math.min(2, segH / 2)}
                      fill={t.color}
                      style={{
                        /**
                         * 图例锁定：非当前分类一律 0.1 透明度（带过渡，不闪）。
                         */
                        opacity: focusType && t.key !== focusType ? 0.1 : 1,
                        transition: 'opacity 0.15s ease',
                        filter: isHover ? 'brightness(0.85)' : 'none',
                      }}
                    />
                  ))
                )}
              </g>
            );

            return (
              <Tooltip
                key={key}
                title={
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {`${dayData.date} ${
                        DAY_NAMES[(dayData.dayOfWeek || 1) - 1]
                      }`}
                    </div>
                    {hits.length === 0 ? (
                      <div style={{ color: 'rgba(255,255,255,.65)' }}>
                        无打卡
                      </div>
                    ) : (
                      hits.map((t) => (
                        <div key={t.key} style={{ lineHeight: '20px' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: t.color,
                              marginRight: 6,
                            }}
                          />
                          {t.label}：{dayData.counts[t.key]} 次
                        </div>
                      ))
                    )}
                    <div
                      style={{
                        marginTop: 4,
                        borderTop: '1px solid rgba(255,255,255,.2)',
                        paddingTop: 4,
                      }}
                    >
                      当日合计 {total} 次
                    </div>
                  </div>
                }
              >
                {cellNode}
              </Tooltip>
            );
          });
        })}
      </svg>

      {/* 图例：悬停某一项→热力图只留该分类，其余降到 0.1 透明度 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 8 }}>
        {types.map((t) => (
          <span
            key={t.key}
            onMouseEnter={() => setFocusType(t.key)}
            onMouseLeave={() => setFocusType(null)}
            style={{
              fontSize: 12,
              color: '#595959',
              userSelect: 'none',
              opacity: focusType && focusType !== t.key ? 0.45 : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 12,
                height: 12,
                borderRadius: 3,
                background: t.color,
                marginRight: 6,
                verticalAlign: -2,
              }}
            />
            {t.label}
          </span>
        ))}
      </div>
    </div>
  );
};

CalendarHeatmap.propTypes = {
  weeks: PropTypes.arrayOf(
    PropTypes.shape({
      weekStart: PropTypes.string.isRequired,
      days: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string.isRequired,
          dayOfWeek: PropTypes.number.isRequired,
          counts: PropTypes.object,
        }),
      ),
    }),
  ),
  types: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
  /** autoFit=false 时的固定边长 */
  cellSize: PropTypes.number,
  /** 堆叠段间隙（格子内部） */
  gap: PropTypes.number,
  /** 是否按容器宽度反推格子边长 */
  autoFit: PropTypes.bool,
  minCell: PropTypes.number,
  maxCell: PropTypes.number,
  /** 行列间隙基准值 */
  cellGap: PropTypes.number,
  /** 宽容器下把余量摊给间隙的上限 */
  maxCellGap: PropTypes.number,
  mode: PropTypes.oneOf(['stack', 'single']),
  /** 单色模式的固定色（深浅表次数） */
  singleColor: PropTypes.string,
  /** 多少次打卡算「最深」，用于归一化深浅 */
  maxCount: PropTypes.number,
  onDateClick: PropTypes.func,
  loading: PropTypes.bool,
  /**
   * 可见数据窗口（'YYYY-MM-DD'）。传了才能把“窗口外的边角天”排除在月份归属之外，
   * 不传则按整列七天算（对完整自然周起点的调用方无影响）。
   */
  rangeStart: PropTypes.string,
  rangeEnd: PropTypes.string,
};

CalendarHeatmap.defaultProps = {
  weeks: [],
  cellSize: 18,
  gap: 1,
  autoFit: true,
  minCell: 10,
  maxCell: 26,
  cellGap: 3,
  maxCellGap: 8,
  mode: 'stack',
  singleColor: '#1890ff',
  maxCount: 4,
  onDateClick: undefined,
  loading: false,
};

export default CalendarHeatmap;
